import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  settlementCreateSettlementAccountMutation,
  settlementGetSettlementAccountQueryKey,
  type SettlementAccountCreate,
} from '@repo/sdk'

import { getStoredUser } from '../../lib/auth-storage'
import { extractUserFacingMessage } from '../../providers/sdkErrorUtils'

interface UseUpsertSettlementAccountReturn {
  upsertAccount: (body: SettlementAccountCreate) => void
  isPending: boolean
}

export function useUpsertSettlementAccount(
  onSuccess: () => void,
): UseUpsertSettlementAccountReturn {
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const { t } = useTranslation()
  const dealerId = getStoredUser()?.userId ?? ''

  const { mutate, isPending } = useMutation({
    ...settlementCreateSettlementAccountMutation(),
    onSuccess: async () => {
      message.success(t('pages.settlements.messages.saveSuccess'))
      await queryClient.invalidateQueries({
        queryKey: settlementGetSettlementAccountQueryKey({ path: { dealerId } }),
      })
      onSuccess()
    },
    // The server's own words first — e.g. a registered account is changed only by administrators
    // (#344, 403 SETTLEMENT_ACCOUNT_CHANGE_ADMIN_ONLY).
    onError: (error) => {
      message.error(extractUserFacingMessage(error) ?? t('pages.settlements.messages.saveFailed'))
    },
  })

  const upsertAccount = (body: SettlementAccountCreate) => {
    mutate({ path: { dealerId }, body })
  }

  return { upsertAccount, isPending }
}
